window.StockCubeCore = window.StockCubeCore || {};

window.StockCubeCore.resolveDataSourceConfig = function (dataSource, filteredStocks, formatNumberFn) {
    let heightValueFn, labelValueFn, maxHeightValue, colorValueFn;
    let sortDirection = 'asc';

    const changePerc = stock => stock.todaysChangePerc || 0;
    const changePctLabel = stock => `${stock.ticker}\n${(stock.todaysChangePerc || 0).toFixed(1)}%`;
    const changePctMax = () => Math.max(...filteredStocks.map(s => Math.abs(s.todaysChangePerc || 0)));

    switch (dataSource) {
        case 'top-gainers':
        case 'top-losers':
        case 'top-gainers-sec':
        case 'top-losers-sec':
        case 'top-dollar-vol-sec':
        case 'top-dollar-vol':
        case 'watchlist-reports':
            heightValueFn = changePerc;
            maxHeightValue = changePctMax();
            labelValueFn = changePctLabel;
            break;

        case 'top-vol-sec':
            heightValueFn = stock => stock.cumulative_volume || 0;
            maxHeightValue = Math.max(...filteredStocks.map(s => Math.abs(s.cumulative_volume || 0)));
            labelValueFn = stock => `${stock.ticker}\n${formatNumberFn(stock.cumulative_volume || 0)}`;
            break;

        case 'top-vol':
            heightValueFn = stock => stock.cumulative_volume / 10000 || 0;
            maxHeightValue = Math.max(...filteredStocks.map(s => Math.abs(s.cumulative_volume / 10000 || 0)));
            labelValueFn = stock => `${stock.ticker}\n${formatNumberFn(stock.cumulative_volume || 0)}`;
            break;

        case 'mmap':
            if (this && this.mmapViewMode === '3d') {
                heightValueFn = stock => {
                    const cap = stock.market_cap || 0;
                    return (stock.todaysChangePerc || 0) < 0 ? -cap : cap;
                };
                maxHeightValue = Math.max(...filteredStocks.map(s => Math.abs(s.market_cap || 0)));
                labelValueFn = stock => `${stock.ticker || ''}\n${formatNumberFn(stock.market_cap || 0)}`;
                colorValueFn = changePerc;
                sortDirection = 'asc';
            } else {
                heightValueFn = changePerc;
                maxHeightValue = changePctMax();
                labelValueFn = changePctLabel;
                sortDirection = 'desc';
            }
            break;

        case 'sip':
        case 'sip-sec':
            heightValueFn = stock => stock.short_interest_pct || 0;
            maxHeightValue = Math.max(...filteredStocks.map(s => Math.abs(s.short_interest_pct || 0)));
            labelValueFn = stock => `${stock.ticker}\n${(stock.short_interest_pct || 0).toFixed(1)}%`;
            break;

        case 'dividend':
        case 'dividend-sec':
            heightValueFn = stock => stock.trail_1y_div_yield_pct || 0;
            maxHeightValue = Math.max(...filteredStocks.map(s => Math.abs(s.trail_1y_div_yield_pct || 0)));
            labelValueFn = stock => `${stock.ticker}\n${(stock.trail_1y_div_yield_pct || 0).toFixed(1)}%`;
            break;

        default:
            heightValueFn = changePerc;
            maxHeightValue = changePctMax();
            labelValueFn = changePctLabel;
            break;
    }

    return { heightValueFn, labelValueFn, maxHeightValue, sortDirection, colorValueFn };
};

window.StockCubeCore.resolveBarGeometry = function (el, containerHeight, numColumns, spacing) {
    const baseBarSize = 0.075;
    const minBarSize = 0.01;
    const maxColumns = 11;

    const geometry = el.getAttribute('geometry') || { width: containerHeight };
    const containerWidth = geometry.width || containerHeight;
    const availableWidth = containerWidth * 0.95;

    const totalWidth = maxColumns * baseBarSize + (maxColumns - 1) * spacing;
    const barWidth = Math.max(
        minBarSize,
        totalWidth <= availableWidth ? baseBarSize : (availableWidth - (maxColumns - 1) * spacing) / maxColumns
    );

    return { barWidth, barDepth: barWidth };
};


window.StockCubeCore.calculateGlobalMaxHeightValue = function (heightValueFn) {
    if (this._cachedGlobalMax) {
        return this._cachedGlobalMax;
    }

    if (!this.currentData || !this.currentData.intervals || !heightValueFn) {
        return { max: 0, hasNegative: false, maxPos: 0, maxNeg: 0 };
    }

    const sectorIds = this.isHasSectors && this.data.sectorIds
        ? this.data.sectorIds.map(Number)
        : null;

    let globalMax = 0;
    let hasNegative = false;
    let maxPos = 0;
    let maxNeg = 0;

    Object.values(this.currentData.intervals).forEach(rawStocks => {
        const stocks = this.applySipFiltersToStocks(rawStocks);
        for (const stock of stocks) {
            if (sectorIds && stock.sector_id != null && !sectorIds.includes(stock.sector_id)) continue;

            const rawVal = heightValueFn(stock);
            if (rawVal < 0) {
                hasNegative = true;
                if (Math.abs(rawVal) > maxNeg) maxNeg = Math.abs(rawVal);
            } else if (rawVal > 0) {
                if (rawVal > maxPos) maxPos = rawVal;
            }

            const val = Math.abs(rawVal);
            if (val > globalMax) globalMax = val;
        }
    });

    const result = { max: globalMax, hasNegative, maxPos, maxNeg };
    this._cachedGlobalMax = result;
    return result;
};

window.StockCubeCore.calculateBarHeight = function (
    heightValue, heightScale, maxHeightValue, targetHeight
) {
    const chartMathFunction = (typeof this.getConfigSetting === 'function')
        ? this.getConfigSetting('chartMathFunction', 'Linear')
        : ((this.configSettings && this.configSettings.value && this.configSettings.value.chartMathFunction) || 'Linear');

    if (chartMathFunction === 'Log') {
        const absValue    = Math.abs(heightValue);
        const absMaxValue = Math.abs(maxHeightValue || 1);
        const logValue    = Math.log10(absValue + 1);
        const maxLogValue = Math.log10(absMaxValue + 1);
        if (maxLogValue === 0) return 0.001;
        let height = (logValue / maxLogValue) * targetHeight;

        if (['top-dollar-vol-sec', 'top-dollar-vol', 'watchlist-reports'].includes(this.data.dataSource)) {
            height /= 2;
        }
        if (heightValue < 0) height = -height;
        return Number.isFinite(height) ? height : 0.001;
    }
    const height = heightValue * heightScale;
    return Number.isFinite(height) ? height : 0.001;
};
